import React, { useEffect, useState, memo, Component } from 'react';
import { Shield, Zap, Check, ArrowRight, AlertTriangle, Download, Smartphone, Lock, ChevronDown, ChevronUp, Clock, XCircle } from 'lucide-react';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { usePurchaseNotifications } from '../hooks/usePurchaseNotifications';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { TestimonialCard } from '../components/TestimonialCard';
import { BonusCard } from '../components/BonusCard';
// FAQ Component
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden bg-white" data-id="element-31">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors" data-id="element-32">
        <span className="font-medium text-gray-900 pr-4" data-id="element-33">{question}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-34" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-35" />}
      </button>
      {isOpen && <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-36">
          <p className="text-gray-700 leading-relaxed" data-id="element-37">{answer}</p>
        </div>}
    </div>;
}
export function LandingPage() {
  const {
    trackEvent
  } = useMetaPixel();
  const {
    notification,
    isVisible
  } = usePurchaseNotifications();
  const scrollToPurchase = () => {
    const element = document.getElementById('compra');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handlePurchaseClick = () => {
    trackEvent('InitiateCheckout');
    window.location.href = 'https://checkout.controlelectrico.shop/cart/42833724571732:1';
  };
  // Track ViewContent when scrolling to bonuses
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          trackEvent('ViewContent', {
            content_name: 'Bonuses Section'
          });
          observer.disconnect();
        }
      });
    }, {
      threshold: 0.5
    });
    const bonusSection = document.getElementById('bonuses');
    if (bonusSection) {
      observer.observe(bonusSection);
    }
    return () => observer.disconnect();
  }, [trackEvent]);
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-800" data-id="element-38">
      {/* Notification Popup */}
      {notification && <PurchaseNotification name={notification.name} city={notification.city} isVisible={isVisible} data-id="element-39" />}

      {/* Header/Nav */}
      <header className="bg-blue-900 text-white py-4 sticky top-0 z-40 shadow-md" data-id="element-40">
        <div className="container mx-auto px-4 flex justify-between items-center" data-id="element-41">
          <div className="flex items-center gap-2" data-id="element-42">
            <Zap className="text-yellow-400 fill-yellow-400 w-6 h-6" data-id="element-43" />
            <span className="font-bold text-lg tracking-tight" data-id="element-44">
              CONTROL ELÉCTRICO
            </span>
          </div>
          <button onClick={scrollToPurchase} className="bg-yellow-500 hover:bg-yellow-400 text-blue-900 font-bold py-2 px-4 rounded text-sm transition-colors" data-id="element-45">
            Ver Oferta
          </button>
        </div>
      </header>

      {/* Hero Section - RESTRUCTURED */}
      <section className="bg-gradient-to-b from-blue-900 to-blue-800 text-white py-16 md:py-24 px-4" data-id="element-46">
        <div className="container mx-auto max-w-4xl" data-id="element-47">
          <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight text-center" data-id="element-48">
            CONTROL ELÉCTRICO
          </h1>

          <div className="mb-8 flex justify-center" data-id="element-49">
            <img src="https://cdn.magicpatterns.com/uploads/tT4YArVnzq89hbKNF75uza/Para_electricistas,_tecnicos_y_mantenimiento_industrial.png" alt="Control Eléctrico Libro Digital" className="w-full max-w-md md:max-w-lg drop-shadow-2xl cursor-pointer" onClick={scrollToPurchase} data-id="element-50" />
          </div>

          <h2 className="text-2xl md:text-3xl font-bold text-yellow-400 mb-12 uppercase tracking-wide text-center" data-id="element-51">
            Diagramas y circuitos de mando
          </h2>

          {/* Main value proposition - no button interruption */}
          <div className="max-w-2xl mx-auto space-y-6 mb-12" data-id="element-52">
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20" data-id="element-53">
              <p className="text-lg md:text-xl font-medium leading-relaxed text-center" data-id="element-54">
                Si trabajás como electricista, técnico o en mantenimiento
                industrial, dejame hacerte una pregunta directa:
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20" data-id="element-55">
              <p className="text-xl md:text-2xl font-bold leading-relaxed text-center" data-id="element-56">
                ¿Alguna vez miraste un diagrama eléctrico y sentiste que "más o
                menos" lo entendías… pero no del todo?
              </p>
            </div>
          </div>

          {/* CTA comes naturally after the question */}
          <div className="text-center" data-id="element-57">
            <button onClick={scrollToPurchase} className="bg-yellow-500 hover:bg-yellow-400 text-blue-900 text-xl md:text-2xl font-bold py-4 px-10 rounded-lg shadow-lg hover:shadow-xl transition-all inline-flex items-center gap-2" data-id="element-58">
              Ver la Solución <ArrowRight className="w-6 h-6" data-id="element-59" />
            </button>
          </div>
        </div>
      </section>

      {/* Problem Section - STRUCTURED CARDS */}
      <section className="py-20 px-4 bg-white" data-id="element-60">
        <div className="container mx-auto max-w-4xl" data-id="element-61">
          <div className="text-center mb-12" data-id="element-62">
            <h3 className="text-3xl font-bold text-blue-900 mb-4" data-id="element-63">
              El problema real
            </h3>
            <p className="text-xl text-gray-600" data-id="element-64">
              Sabés conectar componentes, pero cuando mirás el circuito
              completo...
            </p>
          </div>

          {/* Structured problem cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12" data-id="element-65">
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 text-center" data-id="element-66">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-67">
                <Check className="w-6 h-6 text-blue-600" data-id="element-68" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2" data-id="element-69">Sabés qué es</h4>
              <p className="text-sm text-gray-600" data-id="element-70">
                Un contactor, un térmico, un pulsador
              </p>
            </div>
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 text-center" data-id="element-71">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-72">
                <Check className="w-6 h-6 text-blue-600" data-id="element-73" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2" data-id="element-74">Sabés conectar</h4>
              <p className="text-sm text-gray-600" data-id="element-75">Componentes individuales</p>
            </div>
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center" data-id="element-76">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-77">
                <XCircle className="w-6 h-6 text-red-600" data-id="element-78" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2" data-id="element-79">Pero falta algo</h4>
              <p className="text-sm text-gray-600" data-id="element-80">
                La lógica no termina de cerrar
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-8 rounded-r-xl mb-12" data-id="element-81">
            <p className="text-xl font-bold text-blue-900 mb-4" data-id="element-82">
              Y ese pequeño "no estar 100% seguro" es exactamente lo que genera
              dudas, errores y estrés en el trabajo.
            </p>
          </div>

          {/* Root cause - structured */}
          <div className="mb-12" data-id="element-83">
            <div className="flex items-start gap-4 mb-6" data-id="element-84">
              <AlertTriangle className="text-yellow-500 w-8 h-8 flex-shrink-0 mt-1" data-id="element-85" />
              <div data-id="element-86">
                <h4 className="text-2xl font-bold text-blue-900 mb-3" data-id="element-87">
                  El problema no es que no sepas trabajar
                </h4>
                <p className="text-lg text-gray-700" data-id="element-88">
                  La mayoría de los electricistas no falla por falta de
                  capacidad. Falla porque nadie le enseñó a entender la lógica
                  real del control eléctrico.
                </p>
              </div>
            </div>
          </div>

          {/* What we learned vs what happens - side by side cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-89">
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6" data-id="element-90">
              <h5 className="font-bold text-red-900 mb-4 text-lg" data-id="element-91">
                Aprendimos:
              </h5>
              <div className="space-y-3" data-id="element-92">
                <div className="flex items-center gap-3 bg-white p-3 rounded-lg" data-id="element-93">
                  <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" data-id="element-94"></div>
                  <span className="text-gray-700" data-id="element-95">Circuitos de memoria</span>
                </div>
                <div className="flex items-center gap-3 bg-white p-3 rounded-lg" data-id="element-96">
                  <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" data-id="element-97"></div>
                  <span className="text-gray-700" data-id="element-98">Esquemas copiados</span>
                </div>
                <div className="flex items-center gap-3 bg-white p-3 rounded-lg" data-id="element-99">
                  <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" data-id="element-100"></div>
                  <span className="text-gray-700" data-id="element-101">
                    "Esto siempre se hizo así"
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6" data-id="element-102">
              <h5 className="font-bold text-blue-900 mb-4 text-lg" data-id="element-103">
                Pero cuando cambia:
              </h5>
              <div className="space-y-3" data-id="element-104">
                <div className="flex items-center gap-3 bg-white p-3 rounded-lg" data-id="element-105">
                  <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" data-id="element-106"></div>
                  <span className="text-gray-700" data-id="element-107">El sistema</span>
                </div>
                <div className="flex items-center gap-3 bg-white p-3 rounded-lg" data-id="element-108">
                  <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" data-id="element-109"></div>
                  <span className="text-gray-700" data-id="element-110">La carga</span>
                </div>
                <div className="flex items-center gap-3 bg-white p-3 rounded-lg" data-id="element-111">
                  <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" data-id="element-112"></div>
                  <span className="text-gray-700" data-id="element-113">El tipo de tablero</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-red-50 border-2 border-red-300 rounded-xl p-8 text-center" data-id="element-114">
            <p className="text-2xl font-bold text-red-700" data-id="element-115">
              Aparece la inseguridad. Y la inseguridad, en este rubro, se paga
              caro.
            </p>
          </div>
        </div>
      </section>

      {/* Fear Section - STRUCTURED CARDS */}
      <section className="py-20 px-4 bg-gray-50" data-id="element-116">
        <div className="container mx-auto max-w-4xl" data-id="element-117">
          <h3 className="text-3xl font-bold text-blue-900 mb-4 text-center" data-id="element-118">
            El miedo silencioso que nadie dice
          </h3>
          <p className="text-lg text-gray-600 mb-12 text-center max-w-2xl mx-auto" data-id="element-119">
            No se habla mucho de esto, pero está ahí:
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-120">
            <div className="bg-white border-l-4 border-red-500 p-6 rounded-r-xl shadow-sm" data-id="element-121">
              <div className="flex items-start gap-4" data-id="element-122">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-123">
                  <AlertTriangle className="w-5 h-5 text-red-600" data-id="element-124" />
                </div>
                <div data-id="element-125">
                  <p className="font-bold text-gray-900 mb-1" data-id="element-126">
                    Miedo a quemar un motor
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-127">
                    Un error puede costar miles de pesos
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-l-4 border-red-500 p-6 rounded-r-xl shadow-sm" data-id="element-128">
              <div className="flex items-start gap-4" data-id="element-129">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-130">
                  <AlertTriangle className="w-5 h-5 text-red-600" data-id="element-131" />
                </div>
                <div data-id="element-132">
                  <p className="font-bold text-gray-900 mb-1" data-id="element-133">
                    Miedo a que no responda
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-134">
                    El circuito no funciona como debería
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-l-4 border-red-500 p-6 rounded-r-xl shadow-sm" data-id="element-135">
              <div className="flex items-start gap-4" data-id="element-136">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-137">
                  <AlertTriangle className="w-5 h-5 text-red-600" data-id="element-138" />
                </div>
                <div data-id="element-139">
                  <p className="font-bold text-gray-900 mb-1" data-id="element-140">
                    Miedo a quedar mal
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-141">
                    Frente al cliente o al equipo
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-l-4 border-red-500 p-6 rounded-r-xl shadow-sm" data-id="element-142">
              <div className="flex items-start gap-4" data-id="element-143">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-144">
                  <AlertTriangle className="w-5 h-5 text-red-600" data-id="element-145" />
                </div>
                <div data-id="element-146">
                  <p className="font-bold text-gray-900 mb-1" data-id="element-147">Miedo a perder</p>
                  <p className="text-sm text-gray-600" data-id="element-148">
                    Tiempo, dinero o reputación
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-8 text-center" data-id="element-149">
            <p className="text-lg text-gray-700" data-id="element-150">
              No porque no sepas conectar… sino porque{' '}
              <strong className="text-blue-900" data-id="element-151">
                no entendés completamente el porqué del circuito
              </strong>
              .
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-4 bg-white" data-id="element-152">
        <div className="container mx-auto max-w-3xl" data-id="element-153">
          <h3 className="text-3xl font-bold text-blue-900 mb-6 text-center" data-id="element-154">
            ¿Por qué pasa esto?
          </h3>

          <div className="space-y-6 mb-12" data-id="element-155">
            <p className="text-lg text-gray-700 text-center" data-id="element-156">
              Porque la mayoría de las soluciones no resuelven el problema de
              fondo.
            </p>

            <div className="grid md:grid-cols-3 gap-6" data-id="element-157">
              <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 text-center" data-id="element-158">
                <p className="font-bold text-gray-900 mb-2" data-id="element-159">Libros teóricos</p>
                <p className="text-sm text-gray-600" data-id="element-160">
                  Explican demasiado en abstracto
                </p>
              </div>
              <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 text-center" data-id="element-161">
                <p className="font-bold text-gray-900 mb-2" data-id="element-162">Videos aislados</p>
                <p className="text-sm text-gray-600" data-id="element-163">
                  Muestran casos específicos
                </p>
              </div>
              <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 text-center" data-id="element-164">
                <p className="font-bold text-gray-900 mb-2" data-id="element-165">Experiencia sola</p>
                <p className="text-sm text-gray-600" data-id="element-166">
                  Funciona... hasta que cambia
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-900 text-white p-10 rounded-2xl mb-12 text-center" data-id="element-167">
            <p className="text-2xl md:text-3xl font-bold leading-relaxed" data-id="element-168">
              En ningún lado te enseñan a pensar el control eléctrico como un
              sistema completo.
            </p>
          </div>

          <h2 className="text-3xl font-bold text-blue-900 mb-6 text-center" data-id="element-169">
            La verdad que cambia todo
          </h2>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-8 rounded-r-xl mb-8" data-id="element-170">
            <p className="text-xl font-bold text-gray-900 mb-4" data-id="element-171">
              El problema no es falta de información.
            </p>
            <p className="text-xl font-bold text-blue-900" data-id="element-172">
              El problema es falta de estructura y lógica aplicada.
            </p>
          </div>

          <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-8" data-id="element-173">
            <p className="text-lg text-gray-700 mb-6" data-id="element-174">
              La mayoría aprende componentes sueltos y esquemas aislados. Pero
              nadie explica cómo conectar:
            </p>
            <div className="grid md:grid-cols-3 gap-4" data-id="element-175">
              <div className="bg-white p-4 rounded-lg text-center border-2 border-blue-200" data-id="element-176">
                <p className="font-bold text-blue-900" data-id="element-177">El diagrama</p>
              </div>
              <div className="bg-white p-4 rounded-lg text-center border-2 border-blue-200" data-id="element-178">
                <p className="font-bold text-blue-900" data-id="element-179">La función</p>
              </div>
              <div className="bg-white p-4 rounded-lg text-center border-2 border-blue-200" data-id="element-180">
                <p className="font-bold text-blue-900" data-id="element-181">La aplicación real</p>
              </div>
            </div>
            <p className="text-lg text-gray-700 mt-6 text-center font-medium" data-id="element-182">
              de forma clara y práctica.
            </p>
          </div>
        </div>
      </section>

      {/* Product Intro Section */}
      <section className="py-20 px-4 bg-blue-900 text-white relative overflow-hidden" data-id="element-183">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/circuit-board.png')] opacity-10" data-id="element-184"></div>
        <div className="container mx-auto max-w-4xl relative z-10" data-id="element-185">
          <div className="text-center mb-12" data-id="element-186">
            <span className="bg-yellow-500 text-blue-900 font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-4 inline-block" data-id="element-187">
              La Solución Definitiva
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-188">
              Ahí nace CONTROL ELÉCTRICO
            </h2>
            <p className="text-xl md:text-2xl text-blue-100 max-w-2xl mx-auto" data-id="element-189">
              Un manual práctico creado para ayudarte a:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center mb-12" data-id="element-190">
            <div className="space-y-4" data-id="element-191">
              {['Entender la lógica de los circuitos de mando', 'Leer diagramas con seguridad', 'Aplicar lo aprendido en motores, sistemas y tableros reales', 'Dejar de memorizar esquemas y empezar a comprenderlos'].map((item, index) => <div key={index} className="flex items-start gap-3 bg-blue-800/50 p-4 rounded-lg border border-blue-700" data-id="element-192">
                  <Check className="text-yellow-400 w-6 h-6 flex-shrink-0 mt-0.5" data-id="element-193" />
                  <span className="text-lg" data-id="element-194">{item}</span>
                </div>)}
            </div>
            <div className="flex justify-center" data-id="element-195">
              <img src="https://cdn.magicpatterns.com/uploads/tT4YArVnzq89hbKNF75uza/Para_electricistas,_tecnicos_y_mantenimiento_industrial.png" alt="Manual Control Eléctrico" className="w-full max-w-sm drop-shadow-2xl" data-id="element-196" />
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8 text-center max-w-3xl mx-auto mb-8" data-id="element-197">
            <p className="text-lg italic leading-relaxed" data-id="element-198">
              "Este material funciona como un traductor entre el diagrama y el
              tablero real. No se trata de aprender más cosas. Se trata de
              entender mejor lo que ya usás todos los días."
            </p>
          </div>

          <div className="text-center" data-id="element-199">
            <button onClick={scrollToPurchase} className="bg-yellow-500 hover:bg-yellow-400 text-blue-900 text-xl font-bold py-4 px-10 rounded-lg shadow-lg hover:shadow-xl transition-all" data-id="element-200">
              Ver Oferta Completa
            </button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-white" data-id="element-201">
        <div className="container mx-auto max-w-4xl" data-id="element-202">
          <h3 className="text-3xl font-bold text-blue-900 mb-6 text-center" data-id="element-203">
            Cuando entendés la lógica, todo cambia
          </h3>
          <p className="text-xl text-gray-600 mb-12 text-center max-w-2xl mx-auto" data-id="element-204">
            El diagrama deja de ser un dibujo confuso. El tablero deja de
            generar dudas. El trabajo se vuelve más rápido, más seguro y más
            profesional.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-12" data-id="element-205">
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 text-center" data-id="element-206">
              <Shield className="text-blue-600 w-12 h-12 mx-auto mb-4" data-id="element-207" />
              <p className="font-bold text-gray-900 mb-2" data-id="element-208">Adaptar circuitos</p>
              <p className="text-sm text-gray-600" data-id="element-209">
                Con criterio técnico sólido
              </p>
            </div>
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 text-center" data-id="element-210">
              <Shield className="text-blue-600 w-12 h-12 mx-auto mb-4" data-id="element-211" />
              <p className="font-bold text-gray-900 mb-2" data-id="element-212">Tomar decisiones</p>
              <p className="text-sm text-gray-600" data-id="element-213">
                Con fundamento y seguridad
              </p>
            </div>
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 text-center" data-id="element-214">
              <Shield className="text-blue-600 w-12 h-12 mx-auto mb-4" data-id="element-215" />
              <p className="font-bold text-gray-900 mb-2" data-id="element-216">Trabajar tranquilo</p>
              <p className="text-sm text-gray-600" data-id="element-217">Al energizar sistemas</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-yellow-50 border-2 border-blue-200 rounded-2xl p-10" data-id="element-218">
            <h4 className="text-2xl font-bold mb-6 text-blue-900 text-center" data-id="element-219">
              Pensalo así
            </h4>
            <div className="space-y-4 text-lg text-gray-700" data-id="element-220">
              <p data-id="element-221">
                Muchos electricistas trabajan como{' '}
                <strong data-id="element-222">manejar de memoria un camino</strong>.
              </p>
              <p data-id="element-223">
                Mientras el recorrido es siempre el mismo, funciona. Pero cuando
                cambian las calles…{' '}
                <strong className="text-red-600" data-id="element-224">te perdés</strong>.
              </p>
              <div className="bg-white border-2 border-blue-300 rounded-xl p-6 mt-6" data-id="element-225">
                <p className="font-bold text-xl text-blue-900 mb-2" data-id="element-226">
                  Control Eléctrico no te da un camino fijo. Te da el mapa.
                </p>
                <p className="italic text-gray-700" data-id="element-227">
                  Y cuando tenés el mapa, no importa el recorrido.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-20 px-4 bg-gray-50" data-id="element-228">
        <div className="container mx-auto max-w-4xl" data-id="element-229">
          <h3 className="text-3xl font-bold text-blue-900 mb-4 text-center" data-id="element-230">
            ¿Para quién es este material?
          </h3>
          <p className="text-lg text-gray-600 mb-12 text-center" data-id="element-231">
            Este manual es para vos si:
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-232">
            <div className="bg-white border-2 border-blue-200 p-6 rounded-xl" data-id="element-233">
              <div className="flex items-start gap-4" data-id="element-234">
                <div className="bg-blue-100 p-3 rounded-full flex-shrink-0" data-id="element-235">
                  <Zap className="text-blue-600 w-6 h-6" data-id="element-236" />
                </div>
                <div data-id="element-237">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-238">
                    Sos electricista o técnico eléctrico
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-239">
                    Que busca profesionalizar su trabajo diario
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-blue-200 p-6 rounded-xl" data-id="element-240">
              <div className="flex items-start gap-4" data-id="element-241">
                <div className="bg-blue-100 p-3 rounded-full flex-shrink-0" data-id="element-242">
                  <Zap className="text-blue-600 w-6 h-6" data-id="element-243" />
                </div>
                <div data-id="element-244">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-245">
                    Trabajás con motores y tableros
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-246">
                    Y necesitás entender a fondo los sistemas de mando
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-blue-200 p-6 rounded-xl" data-id="element-247">
              <div className="flex items-start gap-4" data-id="element-248">
                <div className="bg-blue-100 p-3 rounded-full flex-shrink-0" data-id="element-249">
                  <Zap className="text-blue-600 w-6 h-6" data-id="element-250" />
                </div>
                <div data-id="element-251">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-252">
                    Querés dejar de improvisar
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-253">
                    Y tener un método claro de trabajo
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-blue-200 p-6 rounded-xl" data-id="element-254">
              <div className="flex items-start gap-4" data-id="element-255">
                <div className="bg-blue-100 p-3 rounded-full flex-shrink-0" data-id="element-256">
                  <Zap className="text-blue-600 w-6 h-6" data-id="element-257" />
                </div>
                <div data-id="element-258">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-259">
                    Querés sentirte seguro
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-260">
                    Frente a cualquier esquema que te presenten
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-xl" data-id="element-261">
            <p className="text-lg text-gray-700" data-id="element-262">
              No importa si tenés años de experiencia o si estás creciendo en el
              oficio.{' '}
              <strong className="text-blue-900" data-id="element-263">
                Esto no reemplaza tu experiencia: la ordena y la potencia.
              </strong>
            </p>
          </div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section id="bonuses" className="py-20 px-4 bg-white" data-id="element-264">
        <div className="container mx-auto max-w-6xl" data-id="element-265">
          <div className="text-center mb-16" data-id="element-266">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-267">
              Llevate estos Bonos Exclusivos
            </h2>
            <p className="text-xl text-gray-600 mb-6" data-id="element-268">
              Complementos perfectos para tu crecimiento profesional
            </p>
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full font-bold text-sm uppercase tracking-wide" data-id="element-269">
              <Clock className="w-4 h-4" data-id="element-270" />
              Gratis por tiempo limitado
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-271">
            <div onClick={scrollToPurchase} className="cursor-pointer" data-id="element-272">
              <BonusCard number={1} image="https://cdn.magicpatterns.com/uploads/e919PomweLVKfukpiw3LMN/a5a9a24c-b864-40d1-a089-c1af5b14df2a.png" title="Eficiencia en Motores" description="Guía técnica para optimizar el rendimiento y productividad laboral." data-id="element-273" />
            </div>
            <div onClick={scrollToPurchase} className="cursor-pointer" data-id="element-274">
              <BonusCard number={2} image="https://cdn.magicpatterns.com/uploads/aG6G5fo2D5A3ofJLUDyPbN/3b26e923-1811-40cd-a322-fc64b4b38a41.png" title="Plantilla de Presupuesto" description="Formato editable para presentar cotizaciones profesionales y ganar más trabajos." data-id="element-275" />
            </div>
            <div onClick={scrollToPurchase} className="cursor-pointer" data-id="element-276">
              <BonusCard number={3} image="https://cdn.magicpatterns.com/uploads/kuzxSSrN4E2Rig37jDh6hQ/648be31b-ae21-445e-8f7f-8dab35c9db3e.png" title="Protocolo de Verificación" description="Checklist de seguridad previa a la energización para evitar accidentes." data-id="element-277" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-gray-50" data-id="element-278">
        <div className="container mx-auto max-w-6xl" data-id="element-279">
          <h2 className="text-3xl font-bold text-center text-blue-900 mb-12" data-id="element-280">
            Lo que dicen nuestros colegas
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-281">
            <TestimonialCard name="Carlos Méndez" age={34} role="Electricista Industrial" location="Buenos Aires" content="Antes dudaba mucho al ver planos complejos. Este manual me dio la estructura mental que me faltaba. Ahora encaro los tableros con otra seguridad." data-id="element-282" />
            <TestimonialCard name="Roberto Silva" age={42} role="Técnico de Mantenimiento" location="Ciudad de México" content="Llevo 15 años en el rubro y pensé que sabía todo. Este libro me ordenó conceptos que usaba mal. Los bonos son excelentes, sobre todo la plantilla." data-id="element-283" />
            <TestimonialCard name="Juan Pérez" age={29} role="Electricista" location="Bogotá" content="La mejor inversión que hice este año. Explicaciones claras, sin vueltas. Me sirvió para un trabajo grande la semana pasada." data-id="element-284" />
            <TestimonialCard name="Miguel Torres" age={38} role="Técnico Eléctrico" location="Lima" content="El miedo a quemar algo siempre estaba. Entender la lógica de mando me quitó ese peso de encima. Muy recomendado." data-id="element-285" />
            <TestimonialCard name="Fernando González" age={45} role="Supervisor Eléctrico" location="Santiago" content="Compré el manual para mis técnicos y terminamos usándolo todos. Es una herramienta de consulta permanente en el taller." data-id="element-286" />
          </div>
        </div>
      </section>

      {/* Summary Section */}
      <section className="py-20 px-4 bg-white" data-id="element-287">
        <div className="container mx-auto max-w-3xl" data-id="element-288">
          <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center" data-id="element-289">
            Lo que te llevás con Control Eléctrico
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10" data-id="element-290">
            <div className="flex items-center gap-3 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg" data-id="element-291">
              <Check className="text-green-600 w-6 h-6 flex-shrink-0" data-id="element-292" />
              <span className="font-medium" data-id="element-293">
                Más seguridad frente a los diagramas
              </span>
            </div>
            <div className="flex items-center gap-3 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg" data-id="element-294">
              <Check className="text-green-600 w-6 h-6 flex-shrink-0" data-id="element-295" />
              <span className="font-medium" data-id="element-296">Más criterio técnico</span>
            </div>
            <div className="flex items-center gap-3 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg" data-id="element-297">
              <Check className="text-green-600 w-6 h-6 flex-shrink-0" data-id="element-298" />
              <span className="font-medium" data-id="element-299">Menos errores</span>
            </div>
            <div className="flex items-center gap-3 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg" data-id="element-300">
              <Check className="text-green-600 w-6 h-6 flex-shrink-0" data-id="element-301" />
              <span className="font-medium" data-id="element-302">Menos dudas</span>
            </div>
            <div className="flex items-center gap-3 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg md:col-span-2" data-id="element-303">
              <Check className="text-green-600 w-6 h-6 flex-shrink-0" data-id="element-304" />
              <span className="font-medium" data-id="element-305">Más confianza profesional</span>
            </div>
          </div>
          <div className="bg-blue-900 text-white p-8 rounded-2xl text-center" data-id="element-306">
            <p className="text-xl font-medium" data-id="element-307">
              Dejás de copiar sin entender. Dejás de depender de otros. Empezás
              a trabajar con lógica y tranquilidad.
            </p>
          </div>
        </div>
      </section>

      {/* Purchase Section - PROFESSIONAL URGENCY */}
      <section id="compra" className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white" data-id="element-308">
        <div className="container mx-auto max-w-4xl" data-id="element-309">
          {/* Professional urgency indicator */}
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 mb-8 max-w-2xl mx-auto" data-id="element-310">
            <div className="flex items-center justify-center gap-3" data-id="element-311">
              <Clock className="w-5 h-5 text-yellow-700" data-id="element-312" />
              <p className="text-sm font-bold text-yellow-900" data-id="element-313">
                Oferta especial: Bonos gratuitos disponibles por tiempo limitado
              </p>
            </div>
          </div>

          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-center text-gray-900" data-id="element-314">
            Accedé ahora a Control Eléctrico
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto text-center" data-id="element-315">
            Si querés entender de verdad el control eléctrico, trabajar con más
            seguridad y criterio, y dejar atrás la improvisación…
          </p>

          {/* Offer Card - Clean & Professional */}
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl max-w-lg mx-auto border-2 border-gray-200" data-id="element-316">
            {/* Strikethrough Price */}
            <div className="text-center mb-4" data-id="element-317">
              <p className="text-red-600 font-bold text-xl" data-id="element-318">
                Precio regular:{' '}
                <span className="line-through" data-id="element-319">$49.900 ARS</span>
              </p>
            </div>

            {/* Main Title */}
            <div className="text-center mb-6" data-id="element-320">
              <h3 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-2" data-id="element-321">
                OFERTA ESPECIAL
              </h3>
            </div>

            {/* Product Name */}
            <h4 className="text-2xl font-bold text-center text-gray-800 mb-2" data-id="element-322">
              CONTROL ELÉCTRICO
            </h4>
            <p className="text-center text-gray-500 mb-6" data-id="element-323">
              + 3 Bonos Profesionales
            </p>

            {/* Main Price - Prominent */}
            <div className="text-center mb-6" data-id="element-324">
              <div className="inline-block bg-green-50 px-8 py-6 rounded-xl border-2 border-green-500" data-id="element-325">
                <p className="text-6xl md:text-7xl font-extrabold text-green-600" data-id="element-326">
                  $19.990
                </p>
                <p className="text-xl text-gray-600 font-medium mt-2" data-id="element-327">ARS</p>
              </div>
            </div>

            {/* Installments */}
            <p className="text-center text-lg font-semibold text-gray-700 mb-8" data-id="element-328">
              Hasta 3 cuotas sin interés
            </p>

            {/* CTA Button */}
            <button onClick={handlePurchaseClick} className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-white text-xl md:text-2xl font-bold py-5 px-6 rounded-xl shadow-lg hover:shadow-2xl transition-all mb-6" data-id="element-329">
              ACCEDER AHORA
            </button>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 gap-3 text-sm text-gray-600 pt-6 border-t border-gray-200" data-id="element-330">
              <div className="flex items-center gap-2" data-id="element-331">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0" data-id="element-332" />
                <span data-id="element-333">Acceso inmediato</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-334">
                <Download className="w-4 h-4 text-green-600 flex-shrink-0" data-id="element-335" />
                <span data-id="element-336">Descarga digital</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-337">
                <Smartphone className="w-4 h-4 text-green-600 flex-shrink-0" data-id="element-338" />
                <span data-id="element-339">Todos tus dispositivos</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-340">
                <Lock className="w-4 h-4 text-green-600 flex-shrink-0" data-id="element-341" />
                <span data-id="element-342">Pago 100% seguro</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Preview Section */}
      <section className="py-20 px-4 bg-white" data-id="element-343">
        <div className="container mx-auto max-w-6xl" data-id="element-344">
          <div className="text-center mb-12" data-id="element-345">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-346">
              Así es el material por dentro
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-347">
              Ejemplos reales de las páginas que vas a usar en el trabajo diario
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-348">
            {[{
            image: 'https://cdn.magicpatterns.com/uploads/6N9aigZzaNsoRe37pWdoFH/WhatsApp_Image_2026-01-17_at_13.42.51.jpg',
            caption: 'Diagramas explicados paso a paso'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/aRdF2bWiMHEmctuHXsJVR9/WhatsApp_Image_2026-01-17_at_13.42.50_(3).jpg',
            caption: 'Entendé la lógica del circuito, no solo la conexión'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/fuLknpKftbKZSC5DGAQ2tZ/WhatsApp_Image_2026-01-17_at_13.42.50_(2).jpg',
            caption: 'Casos prácticos aplicados al trabajo real'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/aGWc1fPM9DTj6kfR5oKjFr/WhatsApp_Image_2026-01-17_at_13.42.50_(1).jpg',
            caption: 'Material pensado para uso profesional'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/dZNF3VrgumsrSXiMRXLWTh/WhatsApp_Image_2026-01-17_at_13.42.49.jpg',
            caption: 'Explicaciones técnicas claras y directas'
          }].map((item, index) => <div key={index} className="bg-gray-50 border-2 border-gray-200 rounded-xl overflow-hidden hover:border-blue-300 transition-colors" data-id="element-349">
                <div className="aspect-[3/4] relative" data-id="element-350">
                  <img src={item.image} alt={item.caption} className="w-full h-full object-cover" data-id="element-351" />
                </div>
                <div className="p-4" data-id="element-352">
                  <p className="text-sm font-medium text-gray-700 text-center" data-id="element-353">
                    {item.caption}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-20 px-4 bg-blue-50" data-id="element-354">
        <div className="container mx-auto max-w-3xl" data-id="element-355">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border-2 border-blue-200" data-id="element-356">
            <div className="flex items-center justify-center mb-6" data-id="element-357">
              <div className="bg-blue-100 p-4 rounded-full" data-id="element-358">
                <Shield className="w-12 h-12 text-blue-600" data-id="element-359" />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-center text-blue-900 mb-6" data-id="element-360">
              Garantía sin vueltas – 7 días
            </h3>
            <p className="text-lg text-gray-700 text-center leading-relaxed" data-id="element-361">
              Estamos tan seguros de que este material te va a aportar claridad
              real en tu trabajo, que podés probarlo durante{' '}
              <strong data-id="element-362">7 días sin riesgo</strong>. Si no te convence, nos
              escribís y te devolvemos el <strong data-id="element-363">100% del dinero</strong>. Así
              de simple.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-white" data-id="element-364">
        <div className="container mx-auto max-w-3xl" data-id="element-365">
          <div className="text-center mb-12" data-id="element-366">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-367">
              Resolvé acá tus dudas
            </h2>
            <p className="text-lg text-gray-600" data-id="element-368">
              Hacé clic para ver la respuesta que necesitás
            </p>
          </div>

          <div className="space-y-3" data-id="element-369">
            <FAQItem question="¿Qué tipo de contenido incluye CONTROL ELÉCTRICO?" answer="Incluye un manual completo de control eléctrico, con explicación clara de la lógica de los circuitos, diagramas reales y ejemplos aplicados al trabajo profesional." data-id="element-370" />
            <FAQItem question="¿Este material es solo para electricistas con experiencia?" answer="Está pensado tanto para electricistas con experiencia como para técnicos que quieren entender de verdad el control eléctrico y dejar de trabajar por repetición o memoria." data-id="element-371" />
            <FAQItem question="¿Voy a aprender a interpretar diagramas eléctricos?" answer="Sí. El foco principal es ayudarte a interpretar y entender diagramas de mando y control eléctrico, no solo copiarlos." data-id="element-372" />
            <FAQItem question="¿Incluye ejemplos prácticos reales?" answer="Sí. El contenido está orientado a situaciones reales de tableros, motores y circuitos usados en el trabajo diario." data-id="element-373" />
            <FAQItem question="¿En qué formato recibo el material?" answer="Todo el contenido se entrega en formato digital (PDF) con acceso inmediato." data-id="element-374" />
            <FAQItem question="¿Puedo imprimir el material?" answer="Sí. Podés imprimirlo para uso personal." data-id="element-375" />
            <FAQItem question="¿Cuánto tarda en llegar el material después de la compra?" answer="El acceso es inmediato una vez acreditado el pago." data-id="element-376" />
            <FAQItem question="¿Necesito conocimientos avanzados de electrónica o automatización?" answer="No. El material explica los conceptos de forma clara y progresiva, enfocándose en criterio y lógica." data-id="element-377" />
            <FAQItem question="¿El contenido me sirve para trabajar con más seguridad?" answer="Sí. El objetivo es que entiendas qué está pasando en el circuito para trabajar con más seguridad y confianza." data-id="element-378" />
            <FAQItem question="¿Tengo acceso para siempre al material?" answer="Sí. El acceso es permanente." data-id="element-379" />
            <FAQItem question="¿Es un pago único?" answer="Sí. Pagás una sola vez y accedés a todo el contenido y bonos incluidos." data-id="element-380" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-4 text-center text-sm" data-id="element-381">
        <div className="container mx-auto" data-id="element-382">
          <p data-id="element-383">
            &copy; {new Date().getFullYear()} Control Eléctrico. Todos los
            derechos reservados.
          </p>
          <p className="mt-2" data-id="element-384">
            Diagramas y circuitos de mando para profesionales.
          </p>
        </div>
      </footer>
    </div>;
}